import { type FC, useState, useEffect } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import Percent from 'dibs-icons/exports/Percent';

import { UrgencyBanner } from './UrgencyBanner';

const boldWrapper = (chunks: React.ReactNode): React.ReactNode => (
    <span className={dibsCss.textBodySmallHeavy}>{chunks}</span>
);

const messages = {
    limitedTimeSale: (
        <FormattedMessage
            id="dibs-buyer-price.SalePriceBanner.limitedTimeSale"
            defaultMessage="Limited time sale"
        />
    ),
    saleEndsOn: (date: string) => (
        <FormattedMessage
            id="dibs-buyer-price.SalePriceBanner.saleEndsOn"
            defaultMessage="This sale ends on <boldWrapper>{date}</boldWrapper>"
            values={{ date, boldWrapper }}
        />
    ),
    saleEndsInLessThanAWeek: (days: number) => (
        <FormattedMessage
            id="dibs-buyer-price.SalePriceBanner.saleEndsInLessThanAWeek"
            defaultMessage="Don't wait! <boldWrapper>This sale ends in {days} days</boldWrapper>"
            values={{
                days,
                boldWrapper,
            }}
        />
    ),
    saleEndsInLessThanADay: (hours: number, minutes: number, seconds: number) => (
        <FormattedMessage
            id="dibs-buyer-price.SalePriceBanner.saleEndsInLessThanADay"
            defaultMessage="Don't wait! <boldWrapper>This sale ends in {validHours, select, true {{hours}h } other {}}{minutes}m {lessThanAMinute, select, true { {seconds}s} other {}}</boldWrapper>"
            values={{
                validHours: hours > 0,
                lessThanAMinute: minutes < 1,
                hours,
                minutes,
                seconds,
                boldWrapper,
            }}
        />
    ),
    saleEnded: (
        <FormattedMessage
            id="dibs-buyer-price.SalePriceBanner.saleEnded"
            defaultMessage="The sale has ended"
        />
    ),
};

const colorClasses = {
    default: {
        bg: dibsCss.bgYellow50,
        iconFill: dibsCss.fillYellow700,
    },
    alert: {
        bg: dibsCss.bgRed50,
        iconFill: dibsCss.fillRed700,
    },
};

export const SalePriceBanner: FC<{
    saleEndDate?: null | string;
}> = ({ saleEndDate }) => {
    const [currentTime, setCurrentTime] = useState(new Date());

    let saleEndsInLessThanAWeek = false;
    let saleEndsInLessThanADay = false;
    let saleEndsInLessThanAMonth = false;
    let saleHasEnded = false;

    if (saleEndDate) {
        const endDate = new Date(saleEndDate);
        const timeDiff = endDate.getTime() - currentTime.getTime();

        if (timeDiff <= 0) {
            saleHasEnded = true;
        }
    }

    useEffect(() => {
        if (!saleEndsInLessThanADay) {
            return () => {};
        }
        // start counter only if sale ends today
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(interval);
    }, [saleEndsInLessThanADay]);

    let displayMessage;

    const endDate = new Date(saleEndDate || '');
    const timeDiff = endDate.getTime() - currentTime.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    saleHasEnded = timeDiff <= 0;

    if (!saleHasEnded) {
        saleEndsInLessThanAWeek = daysDiff > 0 && daysDiff <= 7;
        saleEndsInLessThanAMonth = daysDiff > 0 && daysDiff <= 30;
        const hoursDiff = timeDiff / (1000 * 3600);
        if (hoursDiff > 0 && hoursDiff <= 24) {
            saleEndsInLessThanADay = true;
        }
    }

    if (saleHasEnded) {
        displayMessage = messages.saleEnded;
    } else if (saleEndsInLessThanADay) {
        const hoursDiff = timeDiff / (1000 * 3600);
        const hours = Math.floor(hoursDiff);
        const minutes = Math.floor((hoursDiff - hours) * 60);
        const seconds = Math.floor((hoursDiff * 60 - hours * 60 - minutes) * 60);
        displayMessage = messages.saleEndsInLessThanADay(hours, minutes, seconds);
    } else if (saleEndsInLessThanAWeek) {
        displayMessage = messages.saleEndsInLessThanAWeek(daysDiff);
    } else if (saleEndsInLessThanAMonth) {
        const formattedDate = endDate.toLocaleDateString(undefined, {
            month: 'long',
            day: 'numeric',
        });
        displayMessage = messages.saleEndsOn(formattedDate);
    } else {
        displayMessage = messages.limitedTimeSale;
    }

    const colors =
        saleEndsInLessThanAWeek || saleHasEnded ? colorClasses.alert : colorClasses.default;

    return (
        <UrgencyBanner backgroundColor={colors.bg}>
            <span className={classnames(dibsCss.mrXsmall, colors.iconFill)}>
                <Percent variation="20" />
            </span>
            <span>{displayMessage}</span>
        </UrgencyBanner>
    );
};

export default SalePriceBanner;
